import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { ownerApi } from "./ownerApi";
import { Shield, LayoutDashboard, Users, Building2, Brain, CreditCard, BarChart3, AlertTriangle, MessageSquare, Bell, ToggleRight, ScrollText, Activity, Settings, LogOut } from "lucide-react";

const NAV = [
  { to: "/owner/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/owner/users", label: "Users", icon: Users, testid: "nav-users" },
  { to: "/owner/teams", label: "Teams", icon: Building2, testid: "nav-teams" },
  { to: "/owner/ai-costs", label: "AI Costs", icon: Brain, testid: "nav-ai-costs" },
  { to: "/owner/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/owner/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/owner/abuse", label: "Abuse", icon: AlertTriangle, testid: "nav-abuse" },
  { to: "/owner/support", label: "Support", icon: MessageSquare, testid: "nav-support" },
  { to: "/owner/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
  { to: "/owner/features", label: "Features", icon: ToggleRight, testid: "nav-features" },
  { to: "/owner/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" },
  { to: "/owner/system", label: "System", icon: Activity, testid: "nav-system" },
  { to: "/owner/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function OwnerLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await ownerApi.get("/auth/me");
        setMe(data);
      } catch { navigate("/owner/login"); }
      try {
        const { data } = await ownerApi.get("/alerts");
        setUnread(data.unread_count || 0);
      } catch {}
    })();
  }, [navigate]);

  const logout = async () => {
    try { await ownerApi.post("/auth/logout"); } catch {}
    localStorage.removeItem("buddy_owner_token");
    localStorage.removeItem("buddy_owner_role");
    navigate("/owner/login");
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0F14", color: "#7A7A72" }}>Loading…</div>;

  return (
    <div className="min-h-screen flex" style={{ background: "#0A0F14", color: "#E8E4DC" }}>
      {/* Sidebar */}
      <aside className="w-56 flex flex-col" style={{ background: "#070B0F", borderRight: "1px solid #1F2A33" }} data-testid="owner-sidebar">
        <div className="p-5 flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#006039" }}><Shield size={15} color="white" /></div>
          <div>
            <p className="text-sm font-bold font-display">Buddy Owner</p>
            <p className="text-[9px] uppercase tracking-wider" style={{ color: "#C9A96E" }}>{me.role}</p>
          </div>
        </div>
        <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} data-testid={n.testid} className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition ${isActive ? "font-semibold" : "hover:bg-[#0F1620]"}`
              } style={({ isActive }) => isActive ? { background: "#0F2A1F", color: "#4FD18E" } : { color: "#9AA5B0" }}>
                <Icon size={14} />
                <span className="flex-1">{n.label}</span>
                {n.to === "/owner/alerts" && unread > 0 && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#C9A96E", color: "#070B0F" }}>{unread}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 mt-auto" style={{ borderTop: "1px solid #1F2A33" }}>
          <div className="flex items-center gap-2 mb-2 px-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: "#006039", color: "white" }}>{me.email[0]?.toUpperCase()}</div>
            <p className="text-[11px] truncate" style={{ color: "#7A7A72" }}>{me.email}</p>
          </div>
          <button onClick={logout} className="flex items-center gap-2 w-full px-3 py-1.5 rounded-lg text-[11px] hover:bg-[#0F1620]" style={{ color: "#9AA5B0" }} data-testid="owner-logout"><LogOut size={12} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
